import { useState } from "react";
import { SearchBar } from "@/components/SearchBar";
import { AlphabeticalIndex } from "@/components/AlphabeticalIndex";
import { TermCard } from "@/components/TermCard";
import { QuickLinks } from "@/components/QuickLinks";
import { PageTransition } from "@/components/PageTransition";
import { AnimatedSection } from "@/components/AnimatedSection";
import { useTerms } from "@/hooks/useTerms";
import { Book, Target, Eye, Sparkles, ArrowRight, ChevronDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import logo from "@/assets/logo.png";
import { useLanguage } from "@/contexts/LanguageContext";

const FEATURED_TERMS_INITIAL = 6;

const Index = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [featuredExpanded, setFeaturedExpanded] = useState(false);
  const { data: featuredTermsAll, isLoading, error, refetch } = useTerms({
    preferFeatured: true,
    featuredOnly: true,
  });

  const hasMoreFeatured =
    (featuredTermsAll?.length ?? 0) > FEATURED_TERMS_INITIAL;
  const displayedFeatured =
    !featuredExpanded && hasMoreFeatured
      ? (featuredTermsAll ?? []).slice(0, FEATURED_TERMS_INITIAL)
      : (featuredTermsAll ?? []);

  return (
    <PageTransition>
      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[hsl(220,40%,13%)] via-[hsl(220,38%,18%)] to-[hsl(218,35%,22%)]">
          {/* Ambient orbs */}
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <div className="bg-primary/15 float absolute -right-32 -top-32 h-72 w-72 rounded-full blur-3xl" />
            <div className="bg-gold/10 float-delayed absolute -bottom-32 -left-32 h-80 w-80 rounded-full blur-3xl" />
          </div>

          {/* Subtle dot pattern */}
          <div
            className="pointer-events-none absolute inset-0 opacity-20"
            style={{
              backgroundImage: `radial-gradient(circle, rgba(255,255,255,0.4) 1px, transparent 1px)`,
              backgroundSize: "32px 32px",
            }}
          />

          <div className="container relative py-5 md:py-14">
            <div className="flex flex-col items-center text-center">
              {/* Logo */}
              <motion.div
                className="relative mb-5"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
              >
                <div className="bg-primary/25 absolute inset-0 scale-150 rounded-full blur-2xl" />
                <img
                  src={logo}
                  alt="Jain Lexicon"
                  className="relative h-16 w-auto object-contain md:h-24"
                />
              </motion.div>

              {/* Tagline */}
              <motion.p
                className="mb-4 max-w-full break-words px-1 text-xs font-semibold uppercase tracking-[0.12em] text-white/80 sm:text-sm sm:tracking-[0.22em] md:text-base md:tracking-[0.3em] lg:text-lg"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.5 }}
              >
                {t("index.heroTagline")}
              </motion.p>

              {/* Advisor Credits */}
              <motion.div
                className="md:mb-7 mb-4 flex flex-col items-center justify-center gap-3 text-white md:flex-row md:gap-10"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.5 }}
              >
                <div className="text-center">
                  <div className="mx-auto mb-1.5 flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5">
                    <Sparkles className="text-gold h-4 w-4" />
                  </div>
                  <p className="text-sm font-semibold uppercase tracking-wider text-white/80">
                    {t("index.creditsAdvisor")}
                  </p>
                  <p className="text-xl font-bold text-white">
                    Dr. Jitendra B. Shah
                  </p>
                  <p className="text-base font-medium text-white/70">
                    Ahmedabad
                  </p>
                </div>
                <div className="hidden h-12 w-px bg-gradient-to-b from-transparent via-white/15 to-transparent md:block" />
                <div className="text-center">
                  <div className="mx-auto mb-1.5 flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5">
                    <Sparkles className="text-gold h-4 w-4" />
                  </div>
                  <p className="text-sm font-semibold uppercase tracking-wider text-white/80">
                    {t("index.creditsEditor")}
                  </p>
                  <p className="text-xl font-bold text-white">Dilip V. Shah</p>
                  <p className="text-base font-medium text-white/70">
                    Philadelphia
                  </p>
                </div>
              </motion.div>

              {/* Search Bar */}
              <motion.div
                className="flex w-full max-w-xl flex-col items-center gap-3 sm:flex-row"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.5 }}
              >
                <div className="w-full flex-1">
                  <SearchBar variant="hero" />
                </div>
                <Button
                  onClick={() => navigate("/contribute")}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 group h-12 w-full px-6 font-semibold shadow-lg transition-all hover:shadow-xl sm:w-auto"
                >
                  <span>{t("index.addTerm")}</span>
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </motion.div>
            </div>
          </div>

          {/* Wave divider */}
          <div className="absolute bottom-0 left-0 right-0">
            <svg
              viewBox="0 0 1440 48"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="block w-full"
            >
              <path
                d="M0 48V24C360 40 720 8 1080 24C1260 32 1350 36 1440 38V48H0Z"
                fill="white"
              />
            </svg>
          </div>
        </section>

        {/* Quick Links */}
        <section className="from-background to-secondary/20 bg-gradient-to-b py-5 md:py-10">
          <div className="container">
            <AnimatedSection className="md:mb-7 mb-4 text-center">
              <h2 className="mb-1 font-serif text-2xl font-bold md:text-3xl">
                <span className="gradient-text">{t("index.quickAccess")}</span>
              </h2>
            </AnimatedSection>
            <QuickLinks />
          </div>
        </section>

        {/* Alphabetical Index */}
        <section className="from-secondary/20 to-background bg-gradient-to-b py-5">
          <div className="container">
            <AnimatedSection>
              <AlphabeticalIndex />
            </AnimatedSection>
          </div>
        </section>

        {/* Featured Terms */}
        <section className="relative overflow-hidden py-5 md:py-12">
          <div className="bg-primary/3 pointer-events-none absolute right-0 top-0 h-80 w-80 -translate-y-1/2 translate-x-1/2 rounded-full blur-3xl" />

          <div className="container relative">
            <AnimatedSection>
              <div className="mb-7 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
                <div>
                  <h2 className="mb-1 font-serif text-2xl font-bold md:text-3xl">
                    <span className="gradient-text">
                      {t("index.featuredTerms")}
                    </span>
                  </h2>
                  <p className="text-muted-foreground text-lg font-medium">
                    {t("index.featuredSubtitle")}
                  </p>
                </div>
                <Button
                  onClick={() => navigate("/browse")}
                  variant="outline"
                  className="border-primary/30 hover:border-primary hover:bg-primary/5 group"
                >
                  <span>{t("index.viewAllTerms")}</span>
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </div>
            </AnimatedSection>

            {isLoading ? (
              <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                {[...Array(6)].map((_, i) => (
                  <div
                    key={i}
                    className="from-secondary/50 to-secondary/30 h-44 animate-pulse rounded-xl bg-gradient-to-br"
                  />
                ))}
              </div>
            ) : error ? (
              <div className="bg-destructive/5 border-destructive/20 rounded-2xl border py-14 text-center">
                <div className="bg-destructive/10 mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full">
                  <Book className="text-destructive h-8 w-8" />
                </div>
                <h3 className="mb-2 font-serif text-xl font-semibold">
                  {t("common.loadErrorTitle")}
                </h3>
                <p className="text-muted-foreground mx-auto mb-6 max-w-md">
                  {t("common.loadErrorBody")}
                </p>
                <Button
                  onClick={() => refetch()}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground h-11 px-8"
                >
                  {t("common.retry")}
                </Button>
              </div>
            ) : featuredTermsAll && featuredTermsAll.length > 0 ? (
              <>
                <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                  {displayedFeatured.map((term, i) => (
                    <TermCard
                      key={term.id}
                      id={term.id}
                      word={term.word}
                      meaningEnglish={term.meaning_english}
                      meaningHindi={term.meaning_hindi}
                      meaningGujarati={term.meaning_gujarati}
                      pronunciation={term.pronunciation}
                      index={i}
                    />
                  ))}
                </div>
                {hasMoreFeatured ? (
                  <div className="mt-8 flex justify-center">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setFeaturedExpanded(v => !v)}
                      className="border-primary/30 hover:border-primary hover:bg-primary/5 gap-1 px-6"
                      aria-expanded={featuredExpanded}
                    >
                      <span>
                        {featuredExpanded
                          ? t("index.showLessFeatured")
                          : t("index.showMoreFeatured")}
                      </span>
                      <ChevronDown
                        className={`h-4 w-4 shrink-0 transition-transform duration-200 ${featuredExpanded ? "rotate-180" : ""}`}
                      />
                    </Button>
                  </div>
                ) : null}
              </>
            ) : (
              <div className="bg-secondary/20 border-border/40 rounded-2xl border py-14 text-center">
                <div className="bg-primary/10 mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full">
                  <Book className="text-primary h-8 w-8" />
                </div>
                <h3 className="mb-2 font-serif text-xl font-semibold">
                  {t("index.noTermsTitle")}
                </h3>
                <p className="text-muted-foreground mx-auto mb-6 max-w-md">
                  {t("index.noTermsBody")}
                </p>
                <Button
                  onClick={() => navigate("/contribute")}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground h-11 px-8"
                >
                  {t("common.addTerm")}
                </Button>
              </div>
            )}
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="bg-background relative py-5 md:py-12">
          <div className="container">
            <div className="grid gap-8 md:grid-cols-2 md:gap-0">
              <AnimatedSection className="border-border/40 text-center md:border-r md:pr-12">
                <div className="relative mb-4 inline-flex h-16 w-16 items-center justify-center">
                  <div className="bg-primary/8 absolute inset-0 rotate-6 rounded-2xl" />
                  <div className="relative flex h-full w-full items-center justify-center rounded-2xl bg-white shadow-md">
                    <Target
                      className="text-primary h-8 w-8"
                      strokeWidth={1.5}
                    />
                  </div>
                </div>
                <h3 className="mb-3 font-serif text-2xl font-bold md:text-3xl">
                  <span className="gradient-text">{t("index.mission")}</span>
                </h3>
                <p className="text-muted-foreground mx-auto max-w-sm text-lg font-medium leading-relaxed md:text-xl">
                  {t("index.missionBody")}
                </p>
              </AnimatedSection>

              <AnimatedSection className="text-center md:pl-12" delay={0.1}>
                <div className="relative mb-4 inline-flex h-16 w-16 items-center justify-center">
                  <div className="bg-accent/8 absolute inset-0 rotate-6 rounded-2xl" />
                  <div className="relative flex h-full w-full items-center justify-center rounded-2xl bg-white shadow-md">
                    <Eye className="text-accent h-8 w-8" strokeWidth={1.5} />
                  </div>
                </div>
                <h3 className="mb-3 font-serif text-2xl font-bold md:text-3xl">
                  <span className="gradient-text">{t("index.vision")}</span>
                </h3>
                <p className="text-muted-foreground mx-auto max-w-sm text-lg font-medium leading-relaxed md:text-xl">
                  {t("index.visionBody")}
                </p>
              </AnimatedSection>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[hsl(220,40%,13%)] via-[hsl(220,38%,18%)] to-[hsl(218,35%,22%)] md:py-12 py-5">
          <div className="pointer-events-none absolute inset-0">
            <div className="bg-primary/8 absolute left-1/4 top-0 h-72 w-72 rounded-full blur-3xl" />
            <div className="bg-gold/5 absolute bottom-0 right-1/4 h-64 w-64 rounded-full blur-3xl" />
          </div>

          <div className="container relative text-center">
            <AnimatedSection>
              <div className="mx-auto max-w-xl">
                <h2 className="mb-3 font-serif text-2xl font-bold text-white md:text-3xl">
                  {t("index.ctaTitle")}
                </h2>
                <p className=" md:mb-8 mb-4 text-base font-medium leading-relaxed text-white/80 sm:text-lg md:text-xl">
                  {t("index.ctaBody")}
                </p>
                <div className="flex items-center justify-center gap-3  flex-row">
                  <Button
                    onClick={() => navigate("/contribute")}
                    size="lg"
                    className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8 font-semibold shadow-lg transition-all hover:shadow-xl"
                  >
                    {t("index.startContributing")}
                  </Button>
                  <Button
                    onClick={() => navigate("/browse")}
                    size="lg"
                    variant="outline"
                    className="h-12 border-white/60 bg-white/10 px-8 font-semibold text-white hover:bg-white/20"
                  >
                    {t("index.browseTermsButton")}
                  </Button>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </section>

        {/* Footer */}
        <footer className="relative bg-[hsl(220,40%,11%)] text-white">
          <div className="via-primary/30 h-px bg-gradient-to-r from-transparent to-transparent" />

          <div className="container">
            <div className="grid grid-cols-1 items-start gap-5 md:gap-8 md:py-10 py-5 md:grid-cols-3">
              {/* Brand */}
              <div className="flex flex-col items-center md:items-start">
                <div
                  onClick={() => navigate("/")}
                  className="mb-3 cursor-pointer transition-opacity hover:opacity-80"
                >
                  <img
                    src={logo}
                    alt="Jain Lexicon"
                    className="h-10 w-auto object-contain"
                  />
                </div>
                <p className="max-w-xs text-center text-base font-medium leading-relaxed text-white/70 md:text-left">
                  {t("footer.blurb")}
                </p>
              </div>

              {/* Links */}
              <div className="flex flex-col items-center">
                <h4 className="mb-4 text-lg font-bold uppercase tracking-wider text-white">
                  {t("footer.quickLinks")}
                </h4>
                <nav className="flex flex-col items-center gap-3">
                  {[
                    { to: "/", label: t("nav.home") },
                    { to: "/browse", label: t("footer.linkBrowseTerms") },
                    { to: "/contribute", label: t("nav.contribute") },
                    { to: "/search", label: t("nav.search") },
                  ].map(link => (
                    <button
                      key={link.to}
                      onClick={() => navigate(link.to)}
                      className="hover:text-primary text-lg font-semibold text-white/90 transition-colors"
                    >
                      {link.label}
                    </button>
                  ))}
                </nav>
              </div>

              {/* Info */}
              <div className="flex flex-col items-center md:items-end">
                <h4 className="mb-4 text-lg font-bold uppercase tracking-wider text-white">
                  {t("footer.about")}
                </h4>
                <div className="space-y-1.5 text-center text-base font-medium md:text-right">
                  <p className="text-white">
                    <span className="font-semibold text-white">
                      {t("footer.advisorLabel")}
                    </span>{" "}
                    <span className="text-white/95">Dr. Jitendra B. Shah</span>
                  </p>
                  <p className="text-white">
                    <span className="font-semibold text-white">
                      {t("footer.editorLabel")}
                    </span>{" "}
                    <span className="text-white/95">Dilip V. Shah</span>
                  </p>
                </div>
              </div>
            </div>

            <div className="border-white/8 flex flex-col items-center justify-between gap-3 border-t py-5 sm:flex-row">
              <p className="text-sm font-medium text-white/60">
                {t("footer.copyright", { year: new Date().getFullYear() })}
              </p>
              <p className="text-sm font-medium text-white/50 md:mb-0 mb-4">
                {t("footer.preserving")}
              </p>
            </div>
          </div>
        </footer>
      </div>
    </PageTransition>
  );
};

export default Index;
